<template>
    <div>
        <card-header title="Support" icon="fa-life-ring"/>

        <card-toolbar>
            <button @click="$openCard('support-ticket-new', {}, card)"><i class="far fa-envelope-open-text"></i>New Ticket</button>

            <template v-slot:right>
                <b-tooltip label="Settings">
                    <button class="icon-only" @click="$openCard('support-settings', {}, card)"><i class="far fa-cog"></i></button>
                </b-tooltip>
            </template>
        </card-toolbar>

        <card-body toolbar>
            <card-list>
                <li class="domain-filters" v-if="domains.length">
                    <button class="filter-btn" :class="{active: selectedDomainId === null}"
                        @click="selectDomain(null)">
                        All
                        <span class="badge" v-if="totalOpenCount()">{{totalOpenCount()}}</span>
                    </button>
                    <button v-for="d in domains" :key="d.id"
                        class="filter-btn" :class="{active: selectedDomainId === d.id}"
                        @click="selectDomain(d.id)">
                        {{d.name}}
                        <span class="badge" v-if="domainOpenCount(d.id)">{{domainOpenCount(d.id)}}</span>
                    </button>
                </li>

                <li class="status-filters">
                    <button v-for="f in filters" :key="f.status"
                        class="filter-btn" :class="{active: selectedStatus === f.status}"
                        @click="selectStatus(f.status)">
                        {{f.label}}
                        <span class="badge" v-if="stats[f.countKey]">{{stats[f.countKey]}}</span>
                    </button>
                </li>

                <search-list-item @action="searchTickets"/>

                <separator-list-item/>

                <support-ticket-tile v-for="ticket in tickets" :key="ticket.id" :card="card" :ticket="ticket"
                    :active="isActive(ticket.id)"
                    @click="$openCard('support-ticket', {ticketId: ticket.id}, card)"
                    @assign="assignToMe(ticket)"
                    @close="closeTicket(ticket)"/>
            </card-list>

            <infinite-loading @infinite="loadTickets" ref="infinite">
                <div slot="no-more"></div>
                <div slot="no-results">No tickets found.</div>
            </infinite-loading>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import SearchListItem from "@/TIER/components/SearchListItem";
    import SeparatorListItem from "@/TIER/components/SeparatorListItem";

    import SupportTicketTile from "../../components/tiles/SupportTicketTile";
    import {client as http} from '@/http_client';
    import {trackViewer, removeViewer} from '@/components/supportViewingTracker';

    export default {
        components: {SeparatorListItem, CardHeader, CardBody, CardList, CardToolbar, SupportTicketTile, SearchListItem},
        props: ['card', 'child', 'props'],
        data() {
            return {
                tickets: [],
                domains: [],
                selectedDomainId: null,
                selectedStatus: 'open',
                paging: {total: 0, start: 0},
                searchQuery: null,
                stats: {},
                domainStats: {},
                filters: [
                    {status: 'open', label: 'Open', countKey: 'open_count'},
                    {status: 'assigned', label: 'Assigned', countKey: 'assigned_count'},
                    {status: 'on_hold', label: 'On Hold', countKey: 'on_hold_count'},
                    {status: 'closed', label: 'Closed', countKey: null}
                ]
            }
        },
        methods: {
            loadDomains() {
                http.get('support/domains').then(response => {
                    this.domains = response.data || [];
                    this.loadDomainStats();
                }).catch(() => {});
            },
            loadDomainStats() {
                for (let d of this.domains) {
                    http.get('support/stats?domain_id=' + d.id, {force: true}).then(response => {
                        this.$set(this.domainStats, d.id, response.data || {});
                    }).catch(() => {});
                }
            },
            domainOpenCount(domainId) {
                let s = this.domainStats[domainId];
                return s ? (s.open_count || 0) : 0;
            },
            totalOpenCount() {
                return this.domains.reduce((sum, d) => sum + this.domainOpenCount(d.id), 0);
            },
            loadStats() {
                let url = 'support/stats';
                if (this.selectedDomainId) url += '?domain_id=' + this.selectedDomainId;
                http.get(url, {force: true}).then(response => {
                    this.stats = response.data || {};
                }).catch(() => {});
            },
            loadTickets($state) {
                let url;
                if (this.searchQuery) {
                    url = 'support/tickets/search/' + encodeURIComponent(this.searchQuery);
                    if (this.selectedDomainId) url += '?domain_id=' + this.selectedDomainId;
                } else {
                    url = 'support/tickets?page=' + Math.floor(this.paging.start / 50) + '&limit=50';
                    if (this.selectedDomainId) url += '&domain_id=' + this.selectedDomainId;
                    if (this.selectedStatus) url += '&status=' + this.selectedStatus;
                }

                http.get(url, {force: true}).then(response => {
                    let data = response.data;

                    if (this.searchQuery) {
                        this.tickets = Array.isArray(data) ? data : (data.tickets || []);
                        this.$emit('loading', false);
                        if ($state) $state.complete();
                    } else {
                        let items = data.tickets || [];
                        this.tickets.push(...items);
                        this.paging.total = data.total || 0;
                        this.paging.start += items.length;

                        this.$emit('loading', false);
                        if ($state) $state.loaded();
                        if ($state && this.tickets.length >= this.paging.total) {
                            $state.complete();
                        }
                    }

                    this.setActive();
                }).catch(err => {
                    console.log(err);
                    this.$emit('loading', false);
                    if ($state) $state.complete();
                });
            },
            searchTickets(q) {
                if (!q || !q.trim()) {
                    this.searchQuery = null;
                } else {
                    this.searchQuery = q.trim();
                }
                this.reset();
            },
            selectDomain(id) {
                this.selectedDomainId = id;
                this.reset();
            },
            selectStatus(status) {
                this.selectedStatus = status;
                this.searchQuery = null;
                this.reset();
            },
            reset() {
                this.tickets = [];
                this.paging = {total: 0, start: 0};
                this.$emit('loading', true);
                this.loadStats();
                this.loadDomainStats();
                if (this.$refs.infinite) {
                    this.$refs.infinite.stateChanger.reset();
                }
            },
            assignToMe(ticket) {
                http.put('support/tickets/' + ticket.id, {assigned_to: this.$store.getters.currentUser.id}).then(() => {
                    let user = this.$store.getters.currentUser;
                    ticket.assigned_to = user.id;
                    ticket.assigned_first_name = user.first_name;
                    ticket.assigned_last_name = user.last_name;
                    ticket.status = 'assigned';
                    this.removeIfFiltered(ticket);
                    this.loadStats();
                    this.loadDomainStats();
                });
            },
            closeTicket(ticket) {
                http.put('support/tickets/' + ticket.id, {status: 'closed'}).then(() => {
                    ticket.status = 'closed';
                    this.removeIfFiltered(ticket);
                    this.loadStats();
                    this.loadDomainStats();
                });
            },
            removeIfFiltered(ticket) {
                if (this.selectedStatus && ticket.status !== this.selectedStatus) {
                    let idx = this.tickets.indexOf(ticket);
                    if (idx !== -1) {
                        this.tickets.splice(idx, 1);
                        this.paging.total = Math.max(0, this.paging.total - 1);
                        this.paging.start = Math.max(0, this.paging.start - 1);
                    }
                }
            },
            isActive(ticketId) {
                return this.child && this.child.definition.component === 'support-ticket' && parseInt(this.child.props.ticketId) === ticketId;
            },
            setActive() {},
            reload() {
                this.reset();
            },
        },
        watch: {
            'child': function () {
                this.setActive();
            }
        },
        sockets: {
            onmessage(event) {
                try {
                    let data = (typeof event.data === 'string') ? JSON.parse(event.data) : event.data;
                    if (data.type === 'support_viewing') {
                        if (data.action === 'viewing') {
                            trackViewer(data.ticketId, data.userId, data.userName);
                        } else if (data.action === 'stop') {
                            removeViewer(data.ticketId, data.userId);
                        }
                        return;
                    }
                    if (data.type === 'support') {
                        let d = data.data || {};
                        if (data.action === 'ticket_updated' && d.id) {
                            let ticket = this.tickets.find(t => t.id === d.id);
                            if (ticket) {
                                if (d.status) ticket.status = d.status;
                                if (d.priority) ticket.priority = d.priority;
                                if (d.assigned_to !== undefined) ticket.assigned_to = d.assigned_to;
                                this.removeIfFiltered(ticket);
                            }
                            this.loadStats();
                            this.loadDomainStats();
                        } else if (data.action === 'new_message' && d.ticketId) {
                            let ticket = this.tickets.find(t => t.id === d.ticketId);
                            if (ticket) {
                                ticket.last_message_at = new Date().toISOString();
                                ticket.message_count = (ticket.message_count || 0) + 1;
                            }
                            this.loadStats();
                            this.loadDomainStats();
                        } else {
                            // new_ticket or unknown action — full reload needed
                            this.reset();
                        }
                    }
                } catch (e) {}
            }
        },
        mounted() {
            this.loadDomains();
            this.loadStats();
        }
    }
</script>

<style lang="scss" scoped>
    .domain-filters {
        padding-top: 10px;
    }

    .domain-filters,
    .status-filters {
        display: flex;
        gap: 6px;
        padding: 4px 10px 10px;
        background-color: #fafafa;
        list-style: none;

        .filter-btn {
            flex: 1;
            padding: 5px 8px;
            font-size: 0.75rem;
            font-weight: 600;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: #fff;
            color: #555;
            cursor: pointer;
            text-align: center;
            white-space: nowrap;

            &:hover {
                background: #f0f0f0;
            }

            &.active {
                background: #28547F;
                color: #fff;
                border-color: #28547F;

                .badge {
                    background: rgba(255,255,255,0.25);
                    color: #fff;
                }
            }

            .badge {
                display: inline-block;
                margin-left: 4px;
                padding: 1px 6px;
                font-size: 0.7rem;
                font-weight: 700;
                border-radius: 10px;
                background: #e0e0e0;
                color: #555;
            }
        }
    }
</style>
